import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Copy, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import type { Campaign } from '@/lib/types';

interface Props {
  campaign: Campaign;
}

function shortUrlBase(): string {
  return (
    import.meta.env.VITE_SHORT_URL_BASE ?? import.meta.env.VITE_INSTALL_BASE_URL
  );
}

function copy(value: string, label: string) {
  navigator.clipboard.writeText(value);
  toast({ title: `${label} copied` });
}

function CopyBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex items-stretch gap-2">
        <code className="flex-1 break-all rounded-md bg-muted p-2 font-mono text-xs">
          {value}
        </code>
        <Button
          type="button"
          variant="outline"
          size="icon"
          aria-label={`Copy ${label}`}
          onClick={() => copy(value, label)}
        >
          <Copy />
        </Button>
      </div>
    </div>
  );
}

export function CampaignArtifacts({ campaign }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);

  const trackingUrl = `${import.meta.env.VITE_INSTALL_BASE_URL}/install?c=${campaign.code}`;
  const shortUrl = `${shortUrlBase()}/c/${campaign.code}`;
  const htmlSnippet = `<a href="${trackingUrl}">Get the app</a>`;
  const mdSnippet = `[Get the App](${trackingUrl})`;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // 512×512 PNG per spec §5.5; uses the short URL since QR readers
    // prefer the shorter payload for tighter modules / faster scans.
    QRCode.toCanvas(canvas, shortUrl, {
      width: 256, // displayed size; downloadDataUrl below renders at 512
      margin: 1,
      errorCorrectionLevel: 'M',
    }).catch(() => {
      /* render failure is non-fatal — copy blocks still work */
    });
    QRCode.toDataURL(shortUrl, {
      width: 512,
      margin: 1,
      errorCorrectionLevel: 'M',
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [shortUrl]);

  function downloadQr() {
    if (!qrDataUrl) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `pulse-${campaign.code}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  return (
    <div className="grid gap-4">
      <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100">
        Campaign <span className="font-mono">{campaign.code}</span> created.
        Share the tracking URL through your channel of choice — the code is
        permanent and cannot be edited later.
      </div>

      <CopyBlock label="Tracking URL" value={trackingUrl} />
      <CopyBlock label="Short URL (for print / SMS / voice)" value={shortUrl} />
      <CopyBlock label="HTML snippet" value={htmlSnippet} />
      <CopyBlock label="Markdown snippet" value={mdSnippet} />

      <div className="grid gap-1.5">
        <Label className="text-xs text-muted-foreground">QR code</Label>
        <div className="flex items-center gap-3 rounded-md border bg-muted/30 p-3">
          <canvas
            ref={canvasRef}
            className="h-32 w-32 rounded bg-white p-1"
            aria-label="Tracking QR code"
          />
          <div className="flex flex-col gap-2">
            <p className="text-xs text-muted-foreground">
              512×512 PNG, encodes the short URL. Print on posters or business
              cards.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!qrDataUrl}
              onClick={downloadQr}
            >
              <Download />
              Download PNG
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
